import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import type { UrlItem } from '../../../types/socket';
import { ImageWithFallback } from '../figma/ImageWithFallback';

export interface Message {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  urls?: UrlItem[];
  isTyping?: boolean;
  isStreaming?: boolean;
  showLeadForm?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  onComplete?: () => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onComplete }) => {
  const isUser = message.sender === 'user';
  const [displayedText, setDisplayedText] = useState('');
  const [isStreamComplete, setIsStreamComplete] = useState(false);
  const onCompleteRef = React.useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  // Split text into parts if it contains double newlines (multi-bubble support)
  const messageParts = message.text.split('\n\n').filter(part => part.trim() !== '');

  // Typing animation effect for bot messages
  useEffect(() => {
    if (!isUser && message.isStreaming && !isStreamComplete) {
      let index = 0;
      setDisplayedText('');

      const interval = setInterval(() => {
        if (index < message.text.length) {
          setDisplayedText(message.text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
          setIsStreamComplete(true);
          onCompleteRef.current?.();
        }
      }, 35);

      return () => clearInterval(interval);
    } else {
      setDisplayedText(message.text);
      setIsStreamComplete(true);
    }
  }, [message.text, message.isStreaming, isUser, isStreamComplete]);

  if (message.isTyping) {
    return (
      <div className="flex justify-start items-end gap-2 mb-4">
        {/* Assistant Avatar for Typing */}
        <div className="w-8 h-8 rounded-full bg-white flex-shrink-0 border border-gray-200 flex items-center justify-center p-1 overflow-hidden shadow-sm">
          <ImageWithFallback
            src="https://ez-learn.global/wp-content/uploads/2023/04/logo.svg"
            alt="AI"
            className="w-full h-full object-contain"
          />
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm inline-block">
          <div className="flex space-x-1 items-center h-5">
            {[0, 0.2, 0.4].map((delay) => (
              <motion.div
                key={delay}
                className="w-1.5 h-1.5 bg-[#0B3D91]/40 rounded-full"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // If streaming, just show the single bubble with animated text
  if (message.isStreaming && !isStreamComplete) {
    return (
      <div className={`flex items-end gap-2 mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-white flex-shrink-0 border border-gray-200 flex items-center justify-center p-1 overflow-hidden shadow-sm">
            <ImageWithFallback
              src="https://ez-learn.global/wp-content/uploads/2023/04/logo.svg"
              alt="AI"
              className="w-full h-full object-contain"
            />
          </div>
        )}
        <div
          className={`
            max-w-[85%] px-4 py-2.5 text-[15px] leading-relaxed relative shadow-md
            ${isUser
              ? 'bg-[#4FABE1] text-white rounded-2xl rounded-br-sm'
              : 'bg-[#0B3D91] text-white rounded-2xl rounded-bl-sm'
            }
          `}
        >
          <p className="whitespace-pre-wrap">{displayedText}</p>
        </div>
      </div>
    );
  }

  // Normal rendering (supports multiple parts)
  return (
    <div className={`flex flex-col gap-2 mb-4 ${isUser ? 'items-end' : 'items-start'}`}>
      {messageParts.map((part, index) => (
        <motion.div
          key={`${message.id}-${index}`}
          initial={{ opacity: 0, scale: 0.95, y: 5 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.25, delay: index * 0.15 }}
          className={`flex items-end gap-2 max-w-[90%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
        >
          {!isUser && (
            <div className="w-8 h-8 rounded-full bg-white flex-shrink-0 border border-gray-200 flex items-center justify-center p-1 overflow-hidden shadow-sm">
              <ImageWithFallback
                src="https://ez-learn.global/wp-content/uploads/2023/04/logo.svg"
                alt="AI"
                className="w-full h-full object-contain"
              />
            </div>
          )}
          <div
            className={`
              px-4 py-2.5 text-[15px] leading-relaxed relative shadow-sm
              ${isUser
                ? 'bg-[#4FABE1] text-white rounded-2xl rounded-br-sm'
                : 'bg-[#0B3D91] text-white rounded-2xl rounded-bl-sm'
              }
            `}
          >
            <p className="whitespace-pre-wrap">{part}</p>

            {/* Show URL buttons only on the last part */}
            {index === messageParts.length - 1 && message.urls && message.urls.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.urls.map((urlItem, uIndex) => (
                  <a
                    key={uIndex}
                    href={urlItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors border border-white/20"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {urlItem.title || 'Learn More'}
                  </a>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};